import { useState } from 'react';
import { RENDER_CONFIG, calculateFontSize } from '../constants/renderConfig';

/**
 * useDownloadHandler Hook
 *
 * Handles PNG/GIF download logic with canvas rendering and blob creation.
 * Manages download state and provides the download function.
 */
export const useDownloadHandler = ({ generateASCII, width, height, columns, backgroundColor, foregroundColor, dimensionMode, customWidth, customHeight }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [exportFormat, setExportFormat] = useState('png');

  const handleDownload = () => {
    // GIF export is handled separately by useGifExport
    if (exportFormat === 'gif') {
      return;
    }

    setIsDownloading(true);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: false });
    const asciiText = generateASCII();
    const lines = asciiText.split('\n');

    let fontSize, charWidth, charHeight, unscaledWidth, unscaledHeight, scale;

    if (dimensionMode === 'custom') {
      // Custom pixel mode: use EXACT dimensions (no scaling)
      scale = 1;
      canvas.width = customWidth;
      canvas.height = customHeight;
      unscaledWidth = customWidth;
      unscaledHeight = customHeight;

      // Calculate font size to fit the custom dimensions
      // Account for character width ratio (characters are fontSize * 0.6 wide)
      const targetFontWidth = customWidth / (width * RENDER_CONFIG.fontWidthRatio);
      const targetFontHeight = customHeight / height;
      fontSize = Math.max(4, Math.min(20, Math.min(targetFontWidth, targetFontHeight)));
      charWidth = fontSize * RENDER_CONFIG.fontWidthRatio;
      charHeight = fontSize;
    } else {
      // Aspect ratio mode: use 3x scale for higher quality
      scale = RENDER_CONFIG.pngExportScale;
      fontSize = calculateFontSize(columns);
      charWidth = fontSize * RENDER_CONFIG.fontWidthRatio;
      charHeight = fontSize;
      unscaledWidth = width * charWidth;
      unscaledHeight = height * charHeight;
      canvas.width = unscaledWidth * scale;
      canvas.height = unscaledHeight * scale;
    }

    // Scale the context for higher resolution rendering (only in aspect ratio mode)
    if (scale > 1) {
      ctx.scale(scale, scale);
    }

    // Enable high-quality text rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Fill background using unscaled dimensions (context is already scaled)
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, unscaledWidth, unscaledHeight);

    ctx.fillStyle = foregroundColor;
    ctx.font = `${fontSize}px monospace`;
    ctx.textBaseline = 'top';

    lines.forEach((line, y) => {
      [...line].forEach((char, x) => {
        ctx.fillText(char, x * charWidth, y * charHeight);
      });
    });

    canvas.toBlob((blob) => {
      setTimeout(() => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `significa-ascii-${Date.now()}.${exportFormat}`;
        a.click();
        URL.revokeObjectURL(url);

        setIsDownloading(false);
        setDownloaded(true);
        setTimeout(() => setDownloaded(false), RENDER_CONFIG.toastDuration);
      }, RENDER_CONFIG.downloadDelay); // Small delay to show animation
    });
  };

  return {
    isDownloading,
    downloaded,
    exportFormat,
    setExportFormat,
    handleDownload
  };
};
